import { createAsyncThunk } from "@reduxjs/toolkit";

import * as signals from "../../../store/signals";
import { CSRF_HEADER, fetchJson, getCSRFToken } from "../../../utils/ajax";
import { IBasket, ICustomerSummary } from "../models";

interface IStopAssistingCustomerResult {
    customer: ICustomerSummary;
    basket: IBasket;
}

/**
 * Stops assisting the current customer as a CSR agent.
 *
 * This ends the CSR assistance session for the specified customer. After stopping,
 * the CSR will no longer be associated with this customer's session. It fetches
 * the updated state to confirm there is no longer an assisted customer.
 *
 * Side effects:
 * - Emits `onBeforeStopAssistingCustomer` signal before stopping
 * - Emits `onAfterStopAssistingCustomer` signal after completion
 * - These signals can be used by other components to react to assistance state changes
 *
 * @param userURL - The API URL of the customer to stop assisting (e.g., "/api/csr/users/123/")
 * @returns Promise resolving to updated customer and basket state (typically both null)
 *
 * @example
 * dispatch(stopAssistingCustomer(customer.url))
 */
export const stopAssistingCustomer = createAsyncThunk(
    "csr/stopAssistingCustomer",
    async (userURL: string): Promise<IStopAssistingCustomerResult> => {
        signals.csr.onBeforeStopAssistingCustomer.emit();
        await fetchJson(`${userURL}stop_assisting/`, {
            method: "POST",
            headers: {
                [CSRF_HEADER]: await getCSRFToken(),
            },
        });
        const [customer, basket] = await Promise.all([
            fetchJson<ICustomerSummary>(
                "/api/csr/users/currently_assisted_user/",
            ),
            fetchJson<IBasket>("/api/csr/baskets/currently_assisted_basket/"),
        ]);
        signals.csr.onAfterStopAssistingCustomer.emit();
        return {
            customer,
            basket,
        };
    },
);
